import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Bell, AlarmClock, Mailbox, Send } from 'lucide-react';
import { formatUsd } from '@/lib/remindersData';

const Stat = ({ label, value, caption, Icon, tone = 'default', testId }) => {
  const accent = {
    default: 'text-[hsl(var(--primary))]',
    success: 'text-[hsl(var(--success))]',
    warning: 'text-[hsl(var(--warning))]',
    danger: 'text-[hsl(var(--destructive))]',
  }[tone];
  const bar = {
    default: 'bg-[hsl(var(--primary))]/60',
    success: 'bg-[hsl(var(--success))]/60',
    warning: 'bg-[hsl(var(--warning))]/60',
    danger: 'bg-[hsl(var(--destructive))]/60',
  }[tone];
  return (
    <Card
      className="relative overflow-hidden border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testId}
    >
      <span aria-hidden className={cn('metallic absolute left-0 top-0 h-full w-[2px]', bar)} />
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] leading-snug sm:text-sm lg:text-xs 2xl:text-sm font-bold uppercase tracking-[0.12em] break-words text-[hsl(var(--primary))]">
            {label}
          </span>
          {Icon ? <Icon size={16} className={cn('opacity-80', accent)} /> : null}
        </div>
        <div
          className="mt-1.5 text-lg sm:text-xl font-semibold tracking-tight tabular-nums text-foreground"
          data-testid={`${testId}-value`}
        >
          {value}
        </div>
        {caption ? (
          <div className="mt-1 text-xs text-muted-foreground">{caption}</div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export const RemindersStatsBar = ({ stats }) => {
  const { t } = useTranslation();
  const {
    count = 0,
    outstanding = 0,
    overdueAmt = 0,
    overdueCount = 0,
    dueCount = 0,
    totalReminders = 0,
  } = stats || {};
  return (
    <div
      className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5"
      data-testid="reminders-stats"
    >
      <Stat
        label={t('reminders.stats.queue')}
        value={count}
        caption={t('reminders.stats.queueCaption', { due: dueCount, overdue: overdueCount })}
        Icon={Bell}
        testId="reminder-stat-queue"
      />
      <Stat
        label={t('reminders.stats.outstanding')}
        value={formatUsd(outstanding)}
        caption={t('reminders.stats.outstandingCaption')}
        Icon={Mailbox}
        tone="warning"
        testId="reminder-stat-outstanding"
      />
      <Stat
        label={t('reminders.stats.overdueTotal')}
        value={formatUsd(overdueAmt)}
        caption={overdueCount > 0 ? t('reminders.stats.overdueCaptionAny') : t('reminders.stats.overdueCaptionNone')}
        Icon={AlarmClock}
        tone="danger"
        testId="reminder-stat-overdue"
      />
      <Stat
        label={t('reminders.stats.sent')}
        value={totalReminders}
        caption={t('reminders.stats.sentCaption')}
        Icon={Send}
        tone="success"
        testId="reminder-stat-sent"
      />
    </div>
  );
};

export default RemindersStatsBar;
